<template>
  <div
    class="pt-autocomplete-container"
  >
    <div class="pt-autocomplete-input-container">
      <pt-form-input
        v-model="inputText"
        :placeholder="placeholder"
        :icon-class="iconClass"
        :icon-position="iconPosition"
        class="pt-autocomplete-input"
        type="text"
        @input="inputChange"
      />
      <span
        v-if="showResetButton"
        class="pt-autocomplete-reset fa fa-stack"
        @click="resetInputText"
      >
        <i class="pt-autocomplete-reset-circle fa fa-circle fa-stack-2x" />
        <i class="pt-autocomplete-reset-cross fa fa-close fa-stack-1x" />
      </span>
    </div>
    <pt-list
      v-if="showList"
      class="mt-1"
      style="max-height: 300px; overflow-y: scroll"
    >
      <pt-list-item
        v-for="(suggestion, suggestionIndex) in autoCompleteList"
        :key="suggestionIndex"
        class="cursor-pointer hover:bg-primary-lighter"
        @list-item-clicked="setItem(suggestion)"
      >{{ showListItem(suggestion) }}</pt-list-item>
      <pt-list-item v-if="!autoCompleteList.length">
        <slot name="noResult">No item found</slot>
      </pt-list-item>
    </pt-list>
  </div>
</template>

<script>
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import filter from 'lodash/filter';
import flatten from '../../../utils/flatten-object';
import { PtFormInput } from '@pathao/ui-components/esm/FormComponents';
import { PtList, PtListItem } from '@pathao/ui-components/esm/List';

const getLookUpObject = (item = {}, fields = []) => {
  if (fields && fields.length) {
    const lookUpObj = {};
    fields.forEach(field => {
      lookUpObj[field] = item[field];
    });
    return flatten(lookUpObj);
  }

  return flatten(item);
};

export default {
  name: 'SuggestionInput',
  components: { PtFormInput, PtList, PtListItem },
  props: {
    list: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: null,
    },
    accessor: {
      type: [String, Function],
      default: null,
    },
    async: {
      type: Boolean,
      default: false,
    },
    iconClass: {
      type: String,
      default: null,
    },
    iconPosition: {
      type: String,
      default: null,
    },
    lookupFields: {
      type: Array,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      inputText: '',
      showList: this.async,
      showResetButton: false,
      selectedItem: null,
    };
  },
  computed: {
    autoCompleteList() {
      if (this.async) {
        return this.list;
      }

      const syncList = filter(this.list, (item) => {
        const lookUpObj = getLookUpObject(item, this.lookupFields);

        let matches = false;

        for (let i in lookUpObj) {
          matches = (function(keyword) {
            let val = lookUpObj[i];

            if (!isString(val)) {
              val += '';
            }

            val = val.replace(/[^\w\s]/gi, '');
            val = val.toLowerCase();

            let correctedKeyword = keyword.replace(/[^\w\s]/gi, '');
            correctedKeyword = correctedKeyword.toLowerCase();
            correctedKeyword = correctedKeyword.trim();

            return val.indexOf(correctedKeyword) > -1;
          })(this.inputText);

          if (matches) {
            break;
          }
        }

        return matches;
      });

      return syncList;
    },
  },
  watch: {
    value() {
      this.inputText = this.value;

      if (!this.inputText) {
        this.selectedItem = null;
        this.showResetButton = false;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    handleDocumentClick(event) {
      if (!this.showList || (this.$el && this.$el.contains(event.target))) {
        return;
      }

      this.showList = false;
    },
    inputChange() {
      this.$emit('search-text-changed', this.inputText);
      this.$emit('input', this.inputText);

      if (!this.async) {
        if (!this.inputText) {
          this.showList = false;
          return null;
        }

        this.showList = true;
      }
    },
    setItem(item) {
      if (!item) {
        return;
      }

      this.inputText = this.showListItem(item);
      this.selectedItem = item;
      this.showList = false;
      this.showResetButton = true;
      this.$emit('autocomplete-item-set', item);
    },
    resetInputText() {
      this.inputText = '';
      this.selectedItem = null;
      this.showResetButton = false;
      this.showList = false;
      this.$emit('autocomplete-cleared');
    },
    showListItem(item) {
      if (isFunction(this.accessor)) {
        return this.accessor(item);
      } else if (isString(this.accessor)) {
        return item[this.accessor] || null;
      }
    },
    getSelectedItem() {
      return this.selectedItem;
    },
  },
};
</script>
